import React, {useState, useEffect, useMemo, useRef} from "react";
import {useNavigate} from "react-router";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {hopnLanguageArray} from "@/utils/functions.js";
import {storage, STORAGE_KEYS, COUNTER_TYPES} from "@/utils/storage";
import {queryClient} from "@/lib/queryClient";
import {SELECTABLES_KEYS} from "@/hooks/useSelectables";
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination} from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {filterSelectedCarDetails} from "@/utils/filesConstants.js";
import {PricingBreakdownList} from "../Components/pricingBreakdownList.jsx";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import { tripNow, formatDHM, parseRideDate } from "@/utils/dateUtils.js";
import {flightService} from "@/services/flightService";
import {isAirportTrip, isMissingRequiredFlightNumber} from "@/utils/airports";
import {customersApiClient} from "@/services/apiClient";
import {getToken, clearToken} from "@/utils/authToken";
import {bookingIdentityStep} from "@/utils/identityGate";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {takeFlightRemovedNotice} from "@/utils/flightRevalidation";
import {FlightNumberPrompt} from "../Components/flightNumberPrompt.jsx";
import {PayAfterDropoffContent} from "./payAfterDropoff.jsx";
import {CancellationModificationRefundContent} from "../Components/cancellationModificationRefund.jsx";
import {
    bigClose,
    vectorEdit,
    earlyMorning,
    afternoon,
    lateNight,
    goIconImg,
    childSmile,
    lBag,
    oversizeBag,
    distanceSvg,
    destinationTimeSvg,
    carSide,
    dogFootPrintBig,
    plane2dIcon,
    infoIcon,
    planCarLogo,
} from "@img/index.js";

export function OrderDetails(props) {
    const navigate = useNavigate();
    const [confirmStep, setConfirmStep] = useState(0);
    // Blocks the submit when a from-airport booking has no flight number — see the CTA.
    // fromAirport with no flight number → the "Enter Flight Number" modal on Continue.
    const [flightPrompt, setFlightPrompt] = useState(false);
    const [authChecking, setAuthChecking] = useState(false);
    const submitWrapperRef = useRef(null);
    const [hopnRideDiff, setHopnRideDiff] = useState(null);
    const [hopnPassengerAddedQuantity, setHopnPassengerAddedQuantity] = useState({});
    const [hopnSelectedChildCarSeat, setHopnSelectedChildCarSeat] = useState([]);
    const [hopnSelectedLuggage, setHopnSelectedLuggage] = useState([]);
    const [hopnSelectedOversized, setHopnSelectedOversized] = useState([]);
    const [hopnSelectedAllLuggage, setHopnSelectedAllLuggage] = useState([]);
    const [editMessage, setEditMessage] = useState(null);
    // {number, reason} when a date / location change removed the flight — shown in the
    // same banner (utils/flightRevalidation).
    const [flightRemoved, setFlightRemoved] = useState(null);
    const [locationTooltip, setLocationTooltip] = useState(false);
    // Desktop dual-mode info popups (mobile branches to props.setStep instead).
    const [payAfterPopup, setPayAfterPopup] = useState(false);
    const [cancelChangeRefundPopup, setCancelChangeRefundPopup] = useState(false);

    const hopnCustomerDate = storage.get(STORAGE_KEYS.RIDE_DATE);
    const hopnRideDateObj = parseRideDate(hopnCustomerDate);
    const hopnRideMonth = hopnRideDateObj ? hopnRideDateObj.toLocaleString('en-US', {month: 'short'}) : '';
    const hopnRideday = hopnRideDateObj ? hopnRideDateObj.toLocaleDateString('en-US', {day: 'numeric'}) * 1 : '';
    const hopnTimeStr = storage.get(STORAGE_KEYS.RIDE_TIME);
    const hopnFormatedTime = hopnTimeStr ? new Date(hopnTimeStr).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }) : '';
    const selectedCarDetails = useMemo(() => filterSelectedCarDetails(), []);

    const saveScrollPosition = () => {
        if (window.innerWidth <= 575) {
            const container = document.querySelector('.hopn-modal-container-inner');
            if (container) storage.set(STORAGE_KEYS.ORDER_DETAILS_SCROLL, String(container.scrollTop));
        }
    };

    const buildSectionFingerprints = () => {
        const passengerTypes = queryClient.getQueryData(SELECTABLES_KEYS.passengerTypes) ?? [];
        const passengers = passengerTypes.map((type) => ({
            id: type.id,
            qty: storage.getCounter(COUNTER_TYPES.PASSENGER_TYPE, type.id),
        }));

        const carSeats = queryClient.getQueryData(SELECTABLES_KEYS.childSeatTypes) ?? [];
        const childSeats = carSeats.map((seat) => ({
            id: seat.id,
            qty: storage.getCounter(COUNTER_TYPES.CAR_SEAT, seat.id),
        }));

        const luggageTypes = queryClient.getQueryData(SELECTABLES_KEYS.luggageTypes) ?? [];
        const luggage = luggageTypes.map((type) => ({
            id: type.id,
            qty: storage.getCounter(COUNTER_TYPES.STANDARD_LUGGAGE, type.id)
                + storage.getCounter(COUNTER_TYPES.OVERSIZED_LUGGAGE, type.id),
        }));

        return {
            passengers: JSON.stringify(passengers),
            childSeats: JSON.stringify(childSeats),
            luggage: JSON.stringify(luggage),
        };
    };

    // Pickup / drop-off / stops as coordinates — what "Locations" means. NOT the route
    // hash: the date & time step re-quotes the route for the new pickup time (traffic-
    // aware drive time), which mints a new hash, so a date-only edit read as "Locations,
    // Date updated".
    const locationsFingerprint = () => JSON.stringify(
        storage.get(STORAGE_KEYS.DESTINATIONS, []).map((d) => {
            const lat = Number(d?.lat);
            const lng = Number(d?.lng);
            return Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)},${lng.toFixed(5)}` : null;
        })
    );

    const saveEditSnapshot = () => {
        storage.set('hopnEditOriginalDate', storage.get(STORAGE_KEYS.RIDE_DATE, ''));
        storage.set('hopnEditOriginalTime', storage.get(STORAGE_KEYS.RIDE_TIME, ''));
        storage.set('hopnEditOriginalCar', storage.get(STORAGE_KEYS.SELECTED_CAR, ''));
        storage.set('hopnEditOriginalFlight', storage.get(STORAGE_KEYS.FLIGHT_NUMBER, ''));
        storage.set('hopnEditOriginalLocations', locationsFingerprint());
        const fp = buildSectionFingerprints();
        storage.set('hopnEditOriginalPassengers', fp.passengers);
        storage.set('hopnEditOriginalChildSeats', fp.childSeats);
        storage.set('hopnEditOriginalLuggage', fp.luggage);
    };

    // "HH:MM" out of a stored RIDE_TIME (a full Date string). Falls back to the raw
    // value when it can't be parsed, so an unexpected format still compares equal to
    // itself rather than silently reporting a change.
    const clockOf = (value) => {
        if (!value) return '';
        const d = new Date(value);
        return Number.isNaN(d.getTime())
            ? String(value)
            : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const clearEditSnapshot = () => {
        storage.remove('hopnEditOriginalDate');
        storage.remove('hopnEditOriginalTime');
        storage.remove('hopnEditOriginalCar');
        storage.remove('hopnEditOriginalFlight');
        storage.remove('hopnEditOriginalLocations');
        storage.remove('hopnEditOriginalPassengers');
        storage.remove('hopnEditOriginalChildSeats');
        storage.remove('hopnEditOriginalLuggage');
    };

    useEffect(() => {
        storage.set(STORAGE_KEYS.EDIT_STEP, false);

        const removedNotice = takeFlightRemovedNotice();
        if (removedNotice) setFlightRemoved(removedNotice);

        const changes = [];
        const origDate = storage.get('hopnEditOriginalDate');
        const origTime = storage.get('hopnEditOriginalTime');
        const origCar = storage.get('hopnEditOriginalCar');
        const origFlight = storage.get('hopnEditOriginalFlight');
        // Raw read: storage.get would JSON-parse the fingerprint back into an array.
        const origLocations = sessionStorage.getItem('hopnEditOriginalLocations');
        const origPassengers = sessionStorage.getItem('hopnEditOriginalPassengers');
        const origChildSeats = sessionStorage.getItem('hopnEditOriginalChildSeats');
        const origLuggage = sessionStorage.getItem('hopnEditOriginalLuggage');

        if (origPassengers !== null) {
            const currentFp = buildSectionFingerprints();

            if (origLocations !== null && origLocations !== locationsFingerprint()) changes.push('locations');

            // Date and time are reported SEPARATELY — one 'datetime' key meant changing
            // only the pickup time still announced "Date & time updated", naming a field
            // the passenger never touched.
            //
            // RIDE_TIME is a full Date string, so a pure DATE change moves it too; compare
            // just its clock part or every date edit would also claim the time changed.
            const dateChanged = origDate !== (storage.get(STORAGE_KEYS.RIDE_DATE, ''));
            const timeChanged = clockOf(origTime) !== clockOf(storage.get(STORAGE_KEYS.RIDE_TIME, ''));
            if (dateChanged && timeChanged) changes.push('datetime');
            else if (dateChanged) changes.push('date');
            else if (timeChanged) changes.push('time');

            // A removed flight gets its own sentence (with the reason) below, not
            // "Flight details updated successfully".
            if (!removedNotice && origFlight !== (storage.get(STORAGE_KEYS.FLIGHT_NUMBER, ''))) changes.push('flight');
            if (origCar !== (storage.get(STORAGE_KEYS.SELECTED_CAR, ''))) changes.push('car');
            if (origPassengers !== currentFp.passengers) changes.push('passenger');
            if (origChildSeats !== currentFp.childSeats) changes.push('childSeats');
            if (origLuggage !== currentFp.luggage) changes.push('luggage');

            clearEditSnapshot();
        }

        if (storage.get('hopnSpecialRequestSavedNotice') === true) {
            storage.remove('hopnSpecialRequestSavedNotice');
            changes.push('specialRequest');
        }

        if (changes.length > 0) setEditMessage(changes);

        const selectedDate = parseRideDate(storage.get(STORAGE_KEYS.RIDE_DATE));
        if (selectedDate) {
            const today = tripNow();
            selectedDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const diffDays = Math.round((selectedDate - today) / (1000 * 60 * 60 * 24));
            if (diffDays === 0) setHopnRideDiff('Today');
            else if (diffDays === 1) setHopnRideDiff('Tomorrow');
            else if (diffDays > 1) setHopnRideDiff(`After ${diffDays} days`);
            else if (diffDays === -1) setHopnRideDiff('Yesterday');
            else setHopnRideDiff(`${Math.abs(diffDays)} days ago`);
        }

        const passengerTypes = queryClient.getQueryData(SELECTABLES_KEYS.passengerTypes) ?? [];
        const pqResult = {};
        passengerTypes.forEach((type) => {
            pqResult[type.id] = storage.getCounter(COUNTER_TYPES.PASSENGER_TYPE, type.id);
        });
        setHopnPassengerAddedQuantity(pqResult);

        const childSeats = queryClient.getQueryData(SELECTABLES_KEYS.childSeatTypes) ?? [];
        const csResult = [];
        childSeats.forEach((type) => {
            const quantity = storage.getCounter(COUNTER_TYPES.CAR_SEAT, type.id);
            if (quantity > 0) csResult.push(type);
        });
        setHopnSelectedChildCarSeat(csResult);

        const luggageTypes = queryClient.getQueryData(SELECTABLES_KEYS.luggageTypes) ?? [];
        const slResult = [];
        luggageTypes.forEach((type) => {
            const quantity = storage.getCounter(COUNTER_TYPES.STANDARD_LUGGAGE, type.id);
            if (quantity > 0) slResult.push(type);
        });
        setHopnSelectedLuggage(slResult);

        const olResult = [];
        luggageTypes.forEach((type) => {
            if (type.category === 'Standard') return;
            const quantity = storage.getCounter(COUNTER_TYPES.OVERSIZED_LUGGAGE, type.id);
            if (quantity > 0) olResult.push(type);
        });
        setHopnSelectedOversized(olResult);

        setHopnSelectedAllLuggage([...slResult, ...olResult]);
    }, []);

    useEffect(() => {
        if (confirmStep !== 2) return;
        const handler = (e) => {
            if (submitWrapperRef.current && !submitWrapperRef.current.contains(e.target)) {
                setConfirmStep(1);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [confirmStep]);

    const destinationUiResponse = storage.get(STORAGE_KEYS.ROUTE_BREAKDOWN);

    // "Pay after drop-off — Continue".
    const continueBooking = async () => {
        // Confirmation tooltip gate disabled per request (tooltip commented out below).
        // if (confirmStep === 0) { setConfirmStep(2); return; }

        // A from-airport ride is unbookable without a flight number (the API 422s).
        // The date/time step no longer asks for one, so this is where it's given: the
        // flight-number modal validates it, stores it, and calls this again
        // (see FlightNumberPrompt).
        if (isMissingRequiredFlightNumber()) {
            setFlightPrompt(true);
            return;
        }

        const token = getToken();
        if (!token) {
            navigate('/signin');
            return;
        }

        setAuthChecking(true);
        const result = await customersApiClient.get('/home');
        setAuthChecking(false);

        if (result.success) {
            // Signed-in account still missing a field the booking gate
            // needs (no phone after Google / Apple) → ask for it HERE,
            // before payment, not after a refused booking.
            setAuthChecking(true);
            const gateStep = await bookingIdentityStep('paymentCards');
            setAuthChecking(false);
            props.setStep(gateStep || 'paymentCards');
        } else {
            clearToken();
            navigate('/signin');
        }
    };

    return (
        <>
            {/* Pay after drop-off popup (desktop) */}
            <div className="upper-modal" style={{display: payAfterPopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true} title="" extraClasses="" totalSteps={0}
                        cancelButton={true} cancelButtonType="icon" backStep={null} showBackButton={false}
                        setStep={props.setStep} closeModal={() => setPayAfterPopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing"><PayAfterDropoffContent/></div>
                    </div>
                </div>
            </div>

            {/* Cancellation / Modification / Refund tabbed popup (desktop) — replaced the
                separate "Cancellation & Change" and "Refund" popups. */}
            <div className="upper-modal" style={{display: cancelChangeRefundPopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true} title="" buttonStyle="elegant"
                        extraClasses="" totalSteps={0}
                        cancelButton={true} cancelButtonType="icon" backStep={null} showBackButton={false}
                        setStep={props.setStep} closeModal={() => setCancelChangeRefundPopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing"><CancellationModificationRefundContent/></div>
                    </div>
                </div>
            </div>

            <ModalHeader
                title=""
                extraClasses="grey"
                totalSteps={0}
                cancelButton={false}
                backStep="selectedCarDetails"
                showBackButton={true}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            {((editMessage && editMessage.length > 0) || flightRemoved) && (
                <div className="hopn-alart-message modalPages-spacing">
                    <div className={`message ${editMessage?.includes('passenger') || flightRemoved ? 'warning' : 'success'}`}>
                        <span>{(() => {
                            const removedText = flightRemoved
                                ? `Flight ${flightRemoved.number} was removed because ${flightRemoved.reason}.`
                                : '';
                            if (!editMessage || editMessage.length === 0) return removedText;
                            const labels = {
                                locations: 'Locations',
                                datetime: 'Date & time',
                                date: 'Date',
                                time: 'Time',
                                flight: 'Flight details',
                                car: 'Vehicle selection',
                                passenger: 'Passengers',
                                childSeats: 'Child car seats',
                                luggage: 'Luggage',
                                specialRequest: 'Special request',
                            };
                            const parts = editMessage.map(k => labels[k]).filter(Boolean);
                            return [parts.join(', ') + ' updated successfully.', removedText].filter(Boolean).join(' ');
                        })()}</span>
                        <button className="hopn-alert-close-btn" onClick={() => { setEditMessage(null); setFlightRemoved(null); }}>
                            <img src={bigClose} alt="Close"/>
                        </button>
                    </div>
                </div>
            )}
            <div className="hopn-customer-order modalPages-spacing pb-8">

                <FlightNumberPrompt
                    open={flightPrompt}
                    onClose={() => setFlightPrompt(false)}
                    onAdded={() => { setFlightPrompt(false); continueBooking(); }}
                    setStep={props.setStep}
                />

                <div className="hopn-header-section">
                    <div className="hopn-date-card">
                        <div className="hopn-date-card-month">{hopnRideMonth}</div>
                        <div className="hopn-date-card-day">{hopnRideday}</div>
                        {hopnRideDiff}
                    </div>
                    <div className="hopn-pickup-card">
                        <div className="hopn-pickup-card-header">
                            <div className="hopn-pickup-card-title">Pick up <span className="hopn-pickup-card-icon">
                                <img
                                    src={(() => {
                                        const timeStr = storage.get(STORAGE_KEYS.RIDE_TIME);
                                        if (!timeStr) return "";
                                        const hours = new Date(timeStr).getHours();
                                        // morning (<12) / afternoon (12–17) / night (18+)
                                        return hours < 12 ? earlyMorning : hours < 18 ? afternoon : lateNight;
                                    })()}
                                    alt="Time"
                                />
                            </span></div>
                        </div>
                        <div className="hopn-pickup-card-info-line">
                            <span className="hopn-pickup-card-time">{hopnFormatedTime}</span>
                        </div>
                    </div>
                    <button className="hopn-customer-edit-btn"
                            onClick={() => {
                                saveScrollPosition();
                                saveEditSnapshot();
                                storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                props.setStep("travelingDateTime");
                            }}>
                        <img src={vectorEdit} alt="Edit"/>
                    </button>
                </div>

                <div className="passenger-quantity-details">
                    <div className="hopn-pickup-card-info-line">
                        <span className="hopn-pickup-card-icon">
                            <img src={goIconImg} alt="Passenger"/>
                        </span>
                        <span className="hopn-pickup-card-passengers">
                            {(() => {
                                if (!hopnPassengerAddedQuantity || Object.keys(hopnPassengerAddedQuantity).length === 0) return null;
                                const quantities = Object.values(hopnPassengerAddedQuantity);
                                const totalPassengers = quantities.reduce((sum, qty) => sum + qty, 0);
                                return totalPassengers > 1 ? `${totalPassengers} passengers` : 'One passenger';
                            })()}
                        </span>
                    </div>
                    <div className="hopn-options-card-inner">
                        <h4 hopn-data-category="order_details" hopn-data-index="child_car_seat">
                            {hopnLanguageArray("order_details", "child_car_seat", "Child car seat")}
                            <img decoding="async" src={childSmile} alt="Child"/>
                        </h4>

                        {hopnSelectedChildCarSeat.length === 0 ? (
                            <p>No child seats selected</p>
                        ) : (
                            <>
                                <div className="for-desktop">
                                    {hopnSelectedChildCarSeat.map((type) => (
                                        <div key={type.id} className="hopn-options-card-item">
                                            <span className="image">
                                                <img src={type.image_url} alt={type.label}/>
                                            </span>
                                            {type.label} : {storage.getCounter(COUNTER_TYPES.CAR_SEAT, type.id)}
                                        </div>
                                    ))}
                                </div>
                                <div className="for-mobile">
                                    <Swiper
                                        modules={[Pagination]}
                                        spaceBetween={15}
                                        pagination={{clickable: true}}
                                        simulateTouch={true}
                                        style={{width: "100%"}}
                                        breakpoints={{
                                            0: {slidesPerView: 1.5},
                                            425: {slidesPerView: 2.5},
                                        }}
                                    >
                                        {hopnSelectedChildCarSeat.map((type, index) => (
                                            <SwiperSlide key={index}>
                                                <div key={type.id} className="hopn-options-card-item">
                                                    <h6>{type.label}</h6>
                                                    <span className="image">
                                                        <h5>{storage.getCounter(COUNTER_TYPES.CAR_SEAT, type.id)}</h5>
                                                        <img src={type.image_url} alt={type.label}/>
                                                    </span>
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            </>
                        )}
                    </div>
                    <button
                        className="hopn-customer-edit-btn"
                        onClick={() => {
                            saveScrollPosition();
                            saveEditSnapshot();
                            storage.set(STORAGE_KEYS.EDIT_STEP, true);
                            props.setStep("passengerQuantity");
                        }}>
                        <img src={vectorEdit} alt="Edit"/>
                    </button>
                </div>

                <div className="hopn-traveling-gui">
                    <div className="hopn-traveling-gui-inner">
                        <div className="hopn-traveling-positions">
                            <div className="hopn-traveling-positions-inner">
                                {destinationUiResponse?.legs?.map((item, index) => {
                                    const hopnTimeStr = storage.get(STORAGE_KEYS.RIDE_TIME);
                                    if (!hopnTimeStr) return null;

                                    let cumulativeTime = new Date(hopnTimeStr);
                                    for (let i = 0; i < index; i++) {
                                        cumulativeTime = new Date(
                                            cumulativeTime.getTime() + destinationUiResponse.legs[i].duration_minutes * 60000
                                        );
                                    }

                                    const legEndTime = new Date(cumulativeTime.getTime() + item.duration_minutes * 60000);
                                    const startTimeStr = cumulativeTime.toLocaleTimeString('en-US', {hour: "numeric", minute: "2-digit", hour12: true});
                                    const endTimeStr = legEndTime.toLocaleTimeString('en-US', {hour: "numeric", minute: "2-digit", hour12: true});

                                    if (index === 0) {
                                        return (
                                            <React.Fragment key={`leg-${index}`}>
                                                <div className="hopn-traveling">
                                                    <div className="hopn-time">{startTimeStr}</div>
                                                    <div className="hopn-description"><span>{item.start_location.address}</span></div>
                                                </div>
                                                <div className={`hopn-traveling ${destinationUiResponse.legs.length > 1 && "car-icon"}`}>
                                                    <div className="hopn-time">
                                                        {endTimeStr} <span>Travel time: {item.duration_text}</span>
                                                    </div>
                                                    <div className="hopn-description"><span>{item.end_location.address}</span></div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }

                                    return (
                                        <div className={`hopn-traveling ${index !== destinationUiResponse.legs.length - 1 ? "car-icon" : ""}`} key={`leg-${index}`}>
                                            <div className="hopn-time">
                                                {endTimeStr} <span>Travel time: {item.duration_text}</span>
                                            </div>
                                            <div className="hopn-description"><span>{item.end_location.address}</span></div>
                                        </div>
                                    );
                                })}

                                <span className="line-style"></span>
                            </div>
                        </div>

                        <div className="hopn-details-card-metrics">
                            <button type="button" className="simple">
                                <img decoding="async" src={distanceSvg} alt="Distance"/> {destinationUiResponse?.distance_km} km
                            </button>
                            <button type="button" className="simple">
                                <img src={destinationTimeSvg} alt="Time"/> {formatDHM(destinationUiResponse?.duration_minutes)}
                            </button>
                            {window.innerWidth <= 575 && (
                                <div className="hopn-customer-edit-btn hopn-submit-btn-wrapper">
                                    <button className="hopn-customer-edit-btn" data-type="route"
                                            onClick={() => {
                                                if (isAirportTrip() && !locationTooltip) {
                                                    setLocationTooltip(true);
                                                    return;
                                                }
                                                saveScrollPosition();
                                                saveEditSnapshot();
                                                setLocationTooltip(false);
                                                storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                                props.closeModal();
                                            }}>
                                        <img src={vectorEdit} alt="Edit"/>
                                    </button>
                                    {locationTooltip && (
                                        <div className="hopn-submit-tooltip visible">
                                            <ModalHeader
                                                title=""
                                                extraClasses="no-space less-font no-background pt-0 pb-0 mt-0 dark"
                                                totalSteps={0}
                                                cancelButton={true}
                                                cancelButtonType="icon"
                                                ignoreEditMode={true}
                                                showBackButton={false}
                                                inline setStep={() => {}}
                                                closeModal={() => setLocationTooltip(false)}
                                            />
                                            <p>After updating locations, you'll be redirected to Date & Time so you can adjust it according to your new locations.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {window.innerWidth > 575 && (
                        <div className="hopn-customer-edit-btn hopn-submit-btn-wrapper">
                            <button className="hopn-customer-edit-btn" data-type="route"
                                    onClick={() => {
                                        if (isAirportTrip() && !locationTooltip) {
                                            setLocationTooltip(true);
                                            return;
                                        }
                                        saveScrollPosition();
                                        saveEditSnapshot();
                                        setLocationTooltip(false);
                                        storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                        props.closeModal();
                                    }}>
                                <img src={vectorEdit} alt="Edit"/>
                            </button>
                            {locationTooltip && (
                                <div className="hopn-submit-tooltip visible">
                                    <ModalHeader
                                        title=""
                                        extraClasses="no-space less-font no-background pt-0 pb-0 mt-0 dark"
                                        totalSteps={0}
                                        cancelButton={true}
                                        cancelButtonType="icon"
                                        ignoreEditMode={true}
                                        showBackButton={false}
                                        inline setStep={() => {}}
                                        closeModal={() => setLocationTooltip(false)}
                                    />
                                    <p>After updating locations, you'll be redirected to Date & Time so you can adjust it according to your new locations.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="hopn-customer-luggage">
                    <div className="hopn-options-section-cards-container">
                        <div className="hopn-options-card for-desktop">
                            <div className="hopn-options-card-area">
                                <div className="hopn-options-card-inner">
                                    <h4 hopn-data-category="order_details" hopn-data-index="luggage">
                                        {hopnLanguageArray("order_details", "luggage", "Luggage")}
                                        <img decoding="async" src={lBag} alt="Large bag"/>
                                    </h4>

                                    {hopnSelectedLuggage.length === 0 ? (
                                        <p>No luggage selected</p>
                                    ) : (
                                        hopnSelectedLuggage.map((item) => (
                                            <div key={item.id} className="hopn-options-card-item">
                                                <span className="image">
                                                    <img src={item.icon_url} alt={item.label}/>
                                                </span>
                                                {item.short_label}: {storage.getCounter(COUNTER_TYPES.STANDARD_LUGGAGE, item.id)}
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="hopn-options-card-inner">
                                    <h4 hopn-data-category="order_details" hopn-data-index="oversized">
                                        {hopnLanguageArray("order_details", "oversized", "Oversized")}
                                        <img decoding="async" src={oversizeBag} alt="Oversized bag"/>
                                    </h4>

                                    {hopnSelectedOversized.length === 0 ? (
                                        <p>No oversized items selected</p>
                                    ) : (
                                        hopnSelectedOversized.map((item) => (
                                            <div key={item.id} className="hopn-options-card-item">
                                                <span className="image">
                                                    <img src={item.image_url} alt={item.label}/>
                                                </span>
                                                {item.label}: {storage.getCounter(COUNTER_TYPES.OVERSIZED_LUGGAGE, item.id)}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div className="hopn-options-card-footer">
                                <div className="hopn-options-card-dots"></div>
                                <button
                                    className="hopn-customer-edit-btn"
                                    onClick={() => {
                                        saveScrollPosition();
                                        saveEditSnapshot();
                                        storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                        props.setStep("passengerLuggage");
                                    }}>
                                    <img src={vectorEdit} alt="Edit"/>
                                </button>
                            </div>
                        </div>
                        <div className="hopn-options-card luggage for-mobile">
                            <div className="hopn-options-card-area">
                                <div className="hopn-options-card-inner">
                                    <h4 hopn-data-category="order_details" hopn-data-index="luggage">
                                        {hopnLanguageArray("order_details", "luggage", "Luggage")}
                                        <img decoding="async" src={lBag} alt="Large bag"/>
                                    </h4>
                                    {hopnSelectedLuggage.length === 0 ? (
                                        <p>No luggage selected</p>
                                    ) : (
                                        <Swiper
                                            modules={[Pagination]}
                                            spaceBetween={15}
                                            pagination={{clickable: true}}
                                            simulateTouch={true}
                                            style={{width: "100%"}}
                                            breakpoints={{
                                                0: {slidesPerView: 1.5},
                                                425: {slidesPerView: 2.5},
                                            }}
                                        >
                                            {hopnSelectedAllLuggage.map((item, index) => {
                                                const isOversized = item.category === 'Oversized' || item.is_oversized;
                                                const counterType = isOversized
                                                    ? COUNTER_TYPES.OVERSIZED_LUGGAGE
                                                    : COUNTER_TYPES.STANDARD_LUGGAGE;
                                                return (
                                                    <SwiperSlide key={index}>
                                                        <div key={item.id} className="hopn-options-card-item">
                                                            <div className="image">
                                                                <img src={item.icon_url || item.image_url} alt={item.label}/>
                                                            </div>
                                                            <div className="content">
                                                                <h6>{item.short_label || item.label}</h6>
                                                                <h6>{storage.getCounter(counterType, item.id)}</h6>
                                                            </div>
                                                        </div>
                                                    </SwiperSlide>
                                                );
                                            })}
                                        </Swiper>
                                    )}
                                </div>
                                <div className="hopn-options-card-footer">
                                    <div className="hopn-options-card-dots"></div>
                                    <button
                                        className="hopn-customer-edit-btn"
                                        onClick={() => {
                                            saveScrollPosition();
                                            saveEditSnapshot();
                                            storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                            props.setStep("passengerLuggage");
                                        }}>
                                        <img src={vectorEdit} alt="Edit"/>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="hopn-options-card luggage">
                            <div className="hopn-options-card-area">
                                <div className="hopn-options-card-inner car">
                                    <h4>Vehicle Detail
                                        <img decoding="async" src={carSide} alt="Oversized bag"/>
                                    </h4>

                                    <div className="hopn-options-card-item">
                                        <span className="image">
                                            <img src={selectedCarDetails.vehicle_image} alt={selectedCarDetails.vehicle_name}/>
                                        </span>
                                        {selectedCarDetails.vehicle_name}: {selectedCarDetails.vehicles_needed}
                                    </div>
                                </div>
                                <div className="hopn-options-card-inner">
                                    <PricingBreakdownList props={props} onAirportFeeClick={() => props.setStep('airportPickupFeeInfo')} onRecalculate={() => {
                                        setHopnPassengerAddedQuantity({...hopnPassengerAddedQuantity});
                                    }}/>
                                </div>
                            </div>
                            <div className="hopn-options-card-footer">
                                <div className="hopn-options-card-dots"></div>
                                <button className="hopn-customer-edit-btn" data-type="luggage" onClick={() => {
                                    saveScrollPosition();
                                    saveEditSnapshot();
                                    storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                    props.setStep("selectedCarDetails");
                                }}>
                                    <img src={vectorEdit} alt="Edit"/>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="hopn-options-section-cards-container">
                        <div className="hopn-options-card">
                            <div className="hopn-options-card-area set-min-auto">
                                <div className="hopn-options-card-inner">
                                    <h4 hopn-data-category="order_details" hopn-data-index="special_request">Special Request
                                        <img decoding="async" src={dogFootPrintBig} alt="Special Request"/>
                                    </h4>
                                    <span className="spacial-request">
                                        {storage.get(STORAGE_KEYS.SPECIAL_REQUEST) ? (
                                            <>
                                                <p>{storage.get(STORAGE_KEYS.SPECIAL_REQUEST)}</p>
                                                {storage.get(STORAGE_KEYS.CARING_PET) === true && (<><br/>Caring Pet : Yes</>)}
                                                {storage.get(STORAGE_KEYS.QUIET_RIDE) === true && (<><br/>Special Quiet Ride : Yes</>)}
                                            </>
                                        ) : "No special request"}
                                    </span>
                                </div>
                            </div>
                            <div className="hopn-options-card-footer">
                                <div className="hopn-options-card-dots"></div>
                                <button className="hopn-customer-edit-btn" onClick={() => {
                                    storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                    props.setStep('specialRequest');
                                }}>
                                    <img src={vectorEdit} alt="Edit"/>
                                </button>
                            </div>
                        </div>
                        {(() => {
                            const flightDetails = storage.get(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
                            if (!flightDetails) return null;
                            return (
                                <div className="hopn-options-card">
                                    <div className="hopn-options-card-area set-min-auto">
                                        <div className="hopn-options-card-inner">
                                            <div className="hopn-airlines-cards hopn-airlines">
                                                <div className="hopn-airlines-card p-0">
                                                    <h4 className="light">{flightDetails.airline?.name || flightDetails.number}
                                                        {flightDetails.aircraft?.model && <span> – {flightDetails.aircraft.model}</span>}</h4>
                                                    <div className="hopn-airlines-card-body">
                                                        <div className="hopn-airlines-from">
                                                            <span className="hopn-airlines-city">
                                                                {flightDetails.departure?.airport?.municipalityName || flightDetails.departure?.airport?.name || '—'} {flightDetails.departure?.airport?.iata ? `(${flightDetails.departure.airport.iata})` : ''}
                                                            </span>
                                                            <span className="hopn-airlines-time">
                                                                {flightDetails.departure?.scheduledTime?.local ? flightService.formatFlightTime(flightDetails.departure.scheduledTime.local) : '—'}
                                                            </span>
                                                            <span className="hopn-airlines-date">
                                                                {flightDetails.departure?.scheduledTime?.local ? flightService.formatFlightDate(flightDetails.departure.scheduledTime.local) : ''}
                                                            </span>
                                                        </div>
                                                        <div className="hopn-airlines-icon">
                                                            <img decoding="async" src={plane2dIcon} alt="Flight"/>
                                                        </div>
                                                        <div className="hopn-airlines-to">
                                                            <span className="hopn-airlines-city">
                                                                {flightDetails.arrival?.airport?.municipalityName || flightDetails.arrival?.airport?.name || '—'} {flightDetails.arrival?.airport?.iata ? `(${flightDetails.arrival.airport.iata})` : ''}
                                                            </span>
                                                            <span className="hopn-airlines-time">
                                                                {flightDetails.arrival?.scheduledTime?.local ? flightService.formatFlightTime(flightDetails.arrival.scheduledTime.local) : '—'}
                                                            </span>
                                                            <span className="hopn-airlines-date">
                                                                {flightDetails.arrival?.scheduledTime?.local ? flightService.formatFlightDate(flightDetails.arrival.scheduledTime.local) : ''}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hopn-options-card-footer">
                                        <div className="hopn-options-card-dots"></div>
                                        {/* Editing the flight goes to the recommended-pickup
                                            chooser — the screen that owns flight entry now.
                                            (It used to open the old smartTimeSelect step,
                                            which is why the edit pencil still showed the
                                            pre-redesign layout.) The chooser's result step
                                            already returns here when EDIT_STEP is set. */}
                                        <button className="hopn-customer-edit-btn" onClick={() => {
                                            saveScrollPosition();
                                            saveEditSnapshot();
                                            storage.set(STORAGE_KEYS.EDIT_STEP, true);
                                            props.setStep('smartPickupRecommendation');
                                        }}>
                                            <img src={vectorEdit} alt="Edit"/>
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                <div className="hopn-info-buttons">
                    <Swiper slidesPerView="auto" spaceBetween={10}>
                        <SwiperSlide>
                            <button type="button" className="hopn-info-btn" onClick={() => {
                                if (window.innerWidth >= 576) { setPayAfterPopup(true); return; }
                                saveScrollPosition();
                                props.setStep("payAfterDropoff");
                            }}>
                                <span>Pay after drop-off</span>
                                <img src={infoIcon} alt="Info"/>
                            </button>
                        </SwiperSlide>
                        {/* One entry point for all three: the tabbed CMR popup replaced the
                            separate "Cancellation & Change" + "Refund" buttons. */}
                        <SwiperSlide>
                            <button type="button" className="hopn-info-btn" onClick={() => {
                                if (window.innerWidth >= 576) { setCancelChangeRefundPopup(true); return; }
                                saveScrollPosition();
                                props.setStep("cancellationModificationRefund");
                            }}>
                                <span>Cancellation, Change & Refund</span>
                                <img src={infoIcon} alt="Info"/>
                            </button>
                        </SwiperSlide>
                    </Swiper>
                </div>

                <div className="hopn-submit-btn-wrapper" ref={submitWrapperRef}>
                    <button
                        className="hopn-add-passenger-button"
                        hopn-data-category="order_details"
                        hopn-data-index="confirm_and_request"
                        disabled={authChecking}
                        onClick={continueBooking}
                    >
                        {authChecking ? <Loader color="#fff"/> : 'Pay after drop-off — Continue'}
                    </button>

                    {/* Confirmation tooltip commented out per request
                    {confirmStep === 2 && (
                        <div className="hopn-submit-confirm-tooltip up">
                            <div className="text-right"><button type="button" className="w-8 h-8 inline-flex items-center justify-center rounded-full cursor-pointer" onClick={() => setConfirmStep(1)} aria-label="Close">
                                <img src={bigClose} alt="Close" className="inline-block"/>
                            </button></div>
                            <h6>Please Ensure</h6>
                            <div className="text-center"><img src={planCarLogo} alt="Verified" className="inline-block"/></div>
                            <p>All booking details are correct — passengers, time, vehicle, luggage, child seats & extras. Incorrect or unlisted details may result in cancellation and full fare charge (no refund).</p>
                            <span>By proceeding, you agree.</span>
                        </div>
                    )}
                    */}
                </div>

                <div className="term-and-condition">
                    <button onClick={() => { saveScrollPosition(); props.setStep('policiesListing'); }}>
                        By proceeding, you confirm that you understand and agree to these terms.
                    </button>
                </div>
            </div>
        </>
    );
}
export default OrderDetails;
